import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, inject, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription, of } from 'rxjs';
import { catchError, debounceTime, startWith, switchMap } from 'rxjs/operators';
import { ReportScheduleService } from '../report-schedule.service';
import { DEFAULT_SECTIONS, ReportScheduleDto, WEEKDAY_OPTIONS } from '../report-schedule.types';
import { browserTimeZone, coerceReportHour, dedupeRecipientEmails, hasReportSection, ianaTimeZones, reportTimeOptions } from '../report-schedule.helpers';
import { AlertService } from '@/app/atoms/alert/alert.service';
import { ViewportOverlayDirective } from '@/app/common/directives/viewport-overlay/viewport-overlay.directive';

const FIELD_CLASS = 'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-primary';

export interface ReportScheduleDialogData {
  briefId: string;
  briefName: string;
  schedule: ReportScheduleDto | null;
  currentUserEmail: string;
}

export type ReportScheduleDialogResult = { action: 'saved'; schedule: ReportScheduleDto } | { action: 'stopped' };

@Component({
  selector: 'mbtr-report-schedule-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './report-schedule-dialog.html',
  styleUrl: './report-schedule-dialog.css',
  host: {
    '(document:keydown.escape)': 'close()',
  },
})
export class ReportScheduleDialog implements OnInit, OnDestroy {
  readonly briefId = input.required<string>();
  readonly briefName = input.required<string>();
  readonly schedule = input<ReportScheduleDto | null>(null);
  readonly currentUserEmail = input('');
  readonly closed = output<ReportScheduleDialogResult | undefined>();

  private readonly formBuilder = inject(FormBuilder);
  private readonly schedules = inject(ReportScheduleService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly alerts = inject(AlertService);
  private readonly subscriptionList = new Subscription();

  readonly weekdays = WEEKDAY_OPTIONS;
  readonly times = reportTimeOptions();
  readonly zones = ianaTimeZones();
  readonly filteredZones = signal<string[]>(this.zones);
  readonly recipients = signal<string[]>([]);
  readonly recipientError = signal<string | null>(null);
  readonly sectionError = signal<string | null>(null);
  readonly nextRunLocal = signal<string>('');
  readonly saving = signal(false);
  readonly zoneQuery = signal('');
  readonly fieldClass = FIELD_CLASS;
  readonly recipientFieldClass = FIELD_CLASS.replace(' w-full', ' min-w-0 flex-1');

  readonly form = this.formBuilder.nonNullable.group({
    dayOfWeek: [1, Validators.required],
    timeOfDay: ['09:00', Validators.required],
    timezone: [browserTimeZone(), Validators.required],
    overview: [DEFAULT_SECTIONS.overview],
    stats: [DEFAULT_SECTIONS.stats],
    topPerformers: [DEFAULT_SECTIONS.topPerformers],
    posts: [DEFAULT_SECTIONS.posts],
    recipientDraft: [''],
  });

  ngOnInit(): void {
    const schedule = this.schedule();
    if (schedule) {
      this.form.patchValue({
        dayOfWeek: schedule.dayOfWeek,
        timeOfDay: coerceReportHour(schedule.timeOfDay),
        timezone: schedule.timezone,
        overview: schedule.sections.overview,
        stats: schedule.sections.stats,
        topPerformers: schedule.sections.topPerformers,
        posts: schedule.sections.posts,
      });
    }
    const existing = schedule?.recipients.map((row) => row.email) ?? [];
    const seed = existing.length ? existing : [this.currentUserEmail()].filter(Boolean);
    this.recipients.set(dedupeRecipientEmails(seed).emails);
    this.subscriptionList.add(
      this.form.valueChanges.pipe(
        startWith(this.form.getRawValue()),
        debounceTime(250),
        switchMap(() => {
          const { dayOfWeek, timeOfDay, timezone } = this.form.getRawValue();
          return this.schedules.nextRun(this.briefId(), { dayOfWeek, timeOfDay, timezone }).pipe(
            catchError(() => of(null)),
          );
        }),
      ).subscribe((preview) => {
        this.nextRunLocal.set(preview?.nextRunLocal ?? '');
        this.cdr.detectChanges();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  close(): void {
    this.closed.emit(undefined);
  }

  dayButtonClass(day: number): string {
    return this.choiceButtonClass(this.form.controls.dayOfWeek.value === day);
  }

  timeButtonClass(time: string): string {
    return this.choiceButtonClass(this.form.controls.timeOfDay.value === time);
  }

  selectTime(time: string): void {
    this.form.controls.timeOfDay.setValue(time);
  }

  private choiceButtonClass(selected: boolean): string {
    const base = 'shrink-0 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium cursor-pointer';
    const state = selected
      ? 'bg-primary text-white dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
    return `${base} ${state}`;
  }

  filterZones(query: string): void {
    this.zoneQuery.set(query);
    const needle = query.trim().toLowerCase();
    const matches = needle ? this.zones.filter((zone) => zone.toLowerCase().includes(needle)).slice(0, 80) : this.zones;
    const selected = this.form.controls.timezone.value;
    this.filteredZones.set(selected && !matches.includes(selected) ? [selected, ...matches] : matches);
  }

  onZoneInput(event: Event): void {
    const value = event.target instanceof HTMLInputElement ? event.target.value : '';
    this.filterZones(value);
    this.cdr.detectChanges();
  }

  selectZone(zone: string): void {
    this.form.controls.timezone.setValue(zone);
    this.cdr.detectChanges();
  }

  keepZoneSearchOpen(event: Event): void {
    event.stopPropagation();
  }

  selectDay(day: number): void {
    this.form.controls.dayOfWeek.setValue(day);
  }

  addRecipient(): void {
    const draft = this.form.controls.recipientDraft.value;
    if (!draft.trim()) {
      return;
    }
    const next = dedupeRecipientEmails([...this.recipients(), draft]);
    this.recipientError.set(next.error);
    if (!next.error) {
      this.recipients.set(next.emails);
      this.form.controls.recipientDraft.setValue('');
    }
    this.cdr.detectChanges();
  }

  removeRecipient(email: string): void {
    this.recipients.update((current) => current.filter((row) => row !== email));
    const next = dedupeRecipientEmails(this.recipients());
    this.recipientError.set(next.error);
  }

  save(event?: Event): void {
    event?.preventDefault();
    if (this.saving()) {
      return;
    }
    const sections = {
      overview: this.form.controls.overview.value,
      stats: this.form.controls.stats.value,
      topPerformers: this.form.controls.topPerformers.value,
      posts: this.form.controls.posts.value,
    };
    const recipients = dedupeRecipientEmails(this.recipients());
    this.recipientError.set(recipients.error);
    this.sectionError.set(hasReportSection(sections) ? null : 'Choose at least one section');
    if (recipients.error || !hasReportSection(sections) || this.form.invalid) {
      this.cdr.detectChanges();
      return;
    }
    this.saving.set(true);
    this.cdr.detectChanges();
    this.subscriptionList.add(
      this.schedules.save(this.briefId(), {
        dayOfWeek: this.form.controls.dayOfWeek.value,
        timeOfDay: this.form.controls.timeOfDay.value,
        timezone: this.form.controls.timezone.value,
        recipients: recipients.emails,
        sections,
      }).subscribe({
        next: (saved) => this.closed.emit({ action: 'saved', schedule: saved }),
        error: () => {
          this.saving.set(false);
          this.recipientError.set('Could not save the schedule. Check the details and try again.');
          this.cdr.detectChanges();
        },
      }),
    );
  }

  stop(): void {
    void this.alerts.confirm({
      title: 'Stop sending this report?',
      message: 'Recipients will no longer receive the weekly email, and existing report links will stop working.',
      confirmText: 'Stop sending',
      cancelText: 'Keep schedule',
      type: 'confirm',
      icon: 'warning',
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.saving.set(true);
      this.cdr.detectChanges();
      this.subscriptionList.add(
        this.schedules.stop(this.briefId()).subscribe({
          next: () => this.closed.emit({ action: 'stopped' }),
          error: () => {
            this.saving.set(false);
            this.cdr.detectChanges();
          },
        }),
      );
    });
  }
}
